import {
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { isTableSectionNode } from 'src/features/templateEdit/section/table/model/tableSection'

/**
 * 表セクション（`sectionType = TABLE`）のソフトリミット。
 *
 * 標準固定値であり、企業別の分岐はない。
 * BE(harami_api_server) の `table_section_validator.go` と同じ値を持つ。
 * FE/BE で二重管理になるが、標準固定値かつ少数のため許容する。
 * 値を変更する場合は BE 側も必ず同時に更新すること。
 */
// 表セクション配下の総ノード数（質問ノード + 条件分岐(Logic)ノード）の上限
export const TABLE_SECTION_MAX_NODE_COUNT = 1400

export type TableSectionSoftLimitViolation = {
  /** 違反した表セクション名（未設定時は「（名称未設定）」） */
  sectionName: string
  /** 現在のノード数 */
  nodeCount: number
}

/**
 * 指定ノード群とその子孫を再帰的に辿り、predicate に一致するノード数を数える。
 * セクションノードは数え方の定義上カウントしないが、その子孫は辿る。
 */
const countNodesInSubtree = (
  templateNodes: TemplateNodesDict,
  nodeIds: number[],
  predicate: (node: TemplateNodeSchema) => boolean,
): number => {
  let count = 0
  for (const nodeId of nodeIds) {
    const node = templateNodes[nodeId]
    if (!node) {
      continue
    }
    if (predicate(node)) {
      count += 1
    }
    count += countNodesInSubtree(templateNodes, node.nodes, predicate)
  }
  return count
}

const isQuestionOrLogicNode = (node: TemplateNodeSchema): boolean =>
  node.type === TemplateNodeTypeEnum.Question ||
  node.type === TemplateNodeTypeEnum.Logic

/**
 * 表セクション配下の総ノード数を数える。
 * 定義（全リポジトリ共通）: 質問ノード + 条件分岐(Logic)ノードの総数。
 * セクションノード（テンプレート行 / 固定行 / 追加行 自身）は数えない。
 */
const countTableSectionNodes = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): number =>
  countNodesInSubtree(templateNodes, tableNode.nodes, isQuestionOrLogicNode)

/**
 * すべての表セクションについて、ノード数上限の超過を静的に検証する。
 * 「上限超過（> limit）」でエラー。境界値（ちょうど上限）は許容する。
 *
 * @returns 上限を超過した違反の一覧（超過がなければ空配列）
 */
export const collectTableSectionSoftLimitViolations = (
  templateNodes: TemplateNodesDict,
): TableSectionSoftLimitViolation[] =>
  Object.values(templateNodes)
    .filter(isTableSectionNode)
    .map(node => ({
      sectionName: node.section?.name?.trim() || '（名称未設定）',
      nodeCount: countTableSectionNodes(node, templateNodes),
    }))
    .filter(({ nodeCount }) => nodeCount > TABLE_SECTION_MAX_NODE_COUNT)
